import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { EmailAuthProvider, GoogleAuthProvider, OAuthProvider, reauthenticateWithCredential, SAMLAuthProvider } from '@angular/fire/auth';
import { GooglePlus } from '@awesome-cordova-plugins/google-plus/ngx';
import { SignInWithApple, ASAuthorizationAppleIDRequest, AppleSignInResponse } from '@awesome-cordova-plugins/sign-in-with-apple/ngx';
import { environment } from 'src/environments/environment';
import firebase from 'firebase/compat/app';
import { StorageService } from './storage.service';
import { FcmService } from './fcm.service';
import { firstValueFrom } from 'rxjs';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { BrowserTab } from '@awesome-cordova-plugins/browser-tab/ngx';
import { Platform } from '@ionic/angular';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private afAuth: AngularFireAuth,
    private storage: StorageService,
    private googlePlus: GooglePlus,
    private apple: SignInWithApple,
    private fcmService: FcmService,
    private afFun: AngularFireFunctions,
    private browserTab: BrowserTab,
    private platform: Platform
  ) { }

  async signInWithApplePlugin(): Promise<AppleSignInResponse> {
    return this.apple.signin({
      requestedScopes: [ASAuthorizationAppleIDRequest.ASAuthorizationScopeFullName, ASAuthorizationAppleIDRequest.ASAuthorizationScopeEmail],
    });
  }

  async getCustomClaims(): Promise<any> {
    const user = await this.afAuth.currentUser;
    if (user) {
      const token = await user.getIdTokenResult();
      return token.claims;
    }
    return null;
  }

  async onAppleLoginSuccess(idToken: string) {
    const provider = new OAuthProvider('apple.com');
    const credential = provider.credential({ idToken });
    await this.afAuth.setPersistence('local');
    return this.afAuth.signInWithCredential(credential);
  }

  async signInWithGooglePlugin() {
    let authData: any;
    let error: any;
    let params: any;
    params = {
      webClientId: environment.webClientId,
      offline: true,
    };
    try {
      await this.googlePlus.logout();
    } catch (error) { }
    try {
      await this.googlePlus.disconnect();
    } catch (error) { }
    try {
      authData = await this.googlePlus.login(params);
    } catch (e) {
      if (e === 12501) {
        error = 'GOOGLE_MODAL_CLOSED_WITHOUT_ACTION: ' + e;
      } else {
        error = 'GOOGLE_LOGIN_ERROR: ' + e;
      }
    }
    return { authData, error };
  }

  async onGoogleLoginSuccess(idToken: any, accessToken: any) {
    const credential = accessToken ? GoogleAuthProvider.credential(idToken, accessToken) : GoogleAuthProvider.credential(idToken);
    await this.afAuth.setPersistence('local');
    return this.afAuth.signInWithCredential(credential);
  }

  getProviderFor(p: string): firebase.auth.AuthProvider | null {
    if (p === 'google') {
      return new GoogleAuthProvider();
    }
    if (p === 'apple') {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      return provider;
    }
    if (p === 'neptun') {
      const provider = new SAMLAuthProvider('saml.szte-neptun');
      return provider;
    }
    return null;
  }

  async signInWithNeptun() {
    const provider = new firebase.auth.SAMLAuthProvider('saml.szte-neptun');
    if (this.platform.is('cordova')) {
      const authUrl = `https://recogera-prod.firebaseapp.com/native-neptun-login`;
      (window as any).cordova.InAppBrowser.open(authUrl, '_system');
    } else {
      this.signInWithPopup(provider);
    }
  }

  async signInWithPopup(provider: any): Promise<firebase.auth.UserCredential> {
    await this.afAuth.setPersistence('local');
    return this.afAuth.signInWithPopup(provider);
  }

  async signInWithRedirect(provider: any): Promise<void> {
    await this.afAuth.setPersistence('local');
    return await this.afAuth.signInWithRedirect(provider);
  }

  async getRedirectResult(): Promise<firebase.auth.UserCredential | null> {
    const result = await this.afAuth.getRedirectResult();
    return result.user ? result : null;
  }

  async getFirebaseAuthDomain(): Promise<string> {
    const app = await this.afAuth.app;
    const options = app.options as any;
    return options.authDomain || 'recogera-prod.firebaseapp.com';
  }

  async getCurrentUser(): Promise<firebase.User | null> {
    return this.afAuth.currentUser;
  }

  async login(email: string, password: string): Promise<firebase.auth.UserCredential> {
    await this.afAuth.setPersistence('local');
    return this.afAuth.signInWithEmailAndPassword(email, password);
  }

  async loginWithCustomToken(customToken: string): Promise<firebase.auth.UserCredential> {
    await this.afAuth.setPersistence('local');
    return this.afAuth.signInWithCustomToken(customToken);
  }

  async fbLogout() {
    await this.afAuth.signOut();
  }

  async logout() {
    try {
      const savedToken = (await this.storage.get('fcm_token')) || null;
      if (savedToken) {
        await this.fcmService.deleteFCMtoken(savedToken);
      }
      await this.fbLogout();
      let temp = localStorage.getItem('lang')!;
      localStorage.clear();
      localStorage.setItem('lang', temp);
    } catch (error) {
      console.error(error);
    }
  }

  currentUserObservable() {
    return this.afAuth.authState;
  }

  async updatePassword(oldPassword: string, password: string, confirmPassword: string): Promise<any> {
    const user = await this.afAuth.currentUser;
    if (user && user.email) {
      try {
        const credential = EmailAuthProvider.credential(user.email, oldPassword);
        await reauthenticateWithCredential(user, credential);

        return await firstValueFrom(
          this.afFun.httpsCallable('userFunctions')({
            action: 'UPDATE_PASSWORD',
            password: password,
            confirmPassword: confirmPassword
          })
        );
      } catch (error) {
        console.error('Error changing password:', error);
        throw error;
      }
    } else {
      throw new Error('No authenticated user found');
    }
  }

  async setNativeNeptunToken(): Promise<any> {
    return await firstValueFrom(
      this.afFun.httpsCallable('userFunctions')({
        action: 'SET_NATIVE_NEPTUN_TOKEN'
      })
    );
  }

  async createNativeNeptunLoginToken(nativeNeptunToken: string) {
    return await firstValueFrom(
      this.afFun.httpsCallable('userFunctions')({
        action: 'GET_NATIVE_NEPTUN_LOGIN_TOKEN',
        nativeNeptunToken
      })
    );
  }
}
